import { checkPropertyAccess } from '@/lib/company-access'
import type { Room } from '@/models'
import { neon } from '@/services/neon'

async function checkRoomAccess(roomId: string, action: string): Promise<Room> {
  const { data: room } = await neon.from('rooms').select('*').eq('id', roomId).single()
  if (!room) throw new Error('Room not found.')

  await checkPropertyAccess((room as Room).property_id, action)
  return room as Room
}

export async function fetchRooms(propertyId: string): Promise<Room[]> {
  await checkPropertyAccess(propertyId, 'view')

  const { data, error } = await neon
    .from('rooms')
    .select('*')
    .eq('property_id', propertyId)
    .order('sort_order', { ascending: true })

  if (error) throw new Error(error.message)
  return (data as Room[]) || []
}

export async function fetchRoomById(id: string): Promise<Room> {
  return checkRoomAccess(id, 'view')
}

export async function createRoom(payload: Partial<Room>): Promise<Room> {
  if (!payload.property_id) throw new Error('Property ID is required.')
  await checkPropertyAccess(payload.property_id, 'create')

  const { data, error } = await neon.from('rooms').insert(payload).select().single()
  if (error) throw new Error(error.message)
  return data as Room
}

export async function updateRoom(id: string, payload: Partial<Room>): Promise<Room> {
  await checkRoomAccess(id, 'update')

  const { data, error } = await neon.from('rooms').update(payload).eq('id', id).select().single()
  if (error) throw new Error(error.message)
  return data as Room
}

export async function deleteRoom(id: string): Promise<void> {
  await checkRoomAccess(id, 'delete')

  const { error } = await neon.from('rooms').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

export async function reorderRooms(roomIds: string[]): Promise<void> {
  if (roomIds.length === 0) return
  await checkRoomAccess(roomIds[0], 'update')

  const { data: targetProperty } = await neon
    .from('rooms')
    .select('property_id')
    .eq('id', roomIds[0])
    .single()
  const propertyId = (targetProperty as { property_id: string } | null)?.property_id

  for (const roomId of roomIds.slice(1)) {
    const { data: room } = await neon.from('rooms').select('property_id').eq('id', roomId).single()
    if (propertyId && (room as { property_id: string } | null)?.property_id !== propertyId) {
      throw new Error('All rooms must belong to the same property.')
    }
  }

  const updates = roomIds.map((id, index) =>
    neon.from('rooms').update({ sort_order: index }).eq('id', id),
  )
  const results = await Promise.all(updates)
  for (const r of results) {
    if ((r as any).error) throw new Error((r as any).error.message)
  }
}
